<script setup lang="ts">
import { EStoreModules } from '@/store/storeType'
import { BaseDialog, StackLayout, StringInputField } from '@rfc/rfc-components-library'
import { useCreateModel } from '@rfc/rfc-components-library/hooks'
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { PRODUCTION_LINE_ROUTES } from '../../../router/routes'
import { EProductionLineActions } from '../../../store/productionLinesStore/ProductionLineStoreTypes'
import { ERouteId } from '../../../types/enum/RouteIdEnum'
import type { ProductionLineCreateModel } from '../../../types/models/ProductionLineModel'

const store = useStore()
const router = useRouter()

type Props = {
    open: boolean
    routeToGo?: string
}
const props = defineProps<Props>()
type Emits = {
    (e: 'close'): void
    (e: 'refresh'): void
}
const emit = defineEmits<Emits>()

const createError = ref<string>()

const createProductionLine = useCreateModel<ProductionLineCreateModel>({
    store,
    stateKey: EStoreModules.PRODUCTION_LINE,
    stateAction: EProductionLineActions.CREATE_PRODUCTION_LINE,
    router,
    routeIdKey: ERouteId.PRODUCTION_LINE_ID,
    routeGoTo: props.routeToGo ?? PRODUCTION_LINE_ROUTES.baseUrl,
    afterCreateCb: () => {
        emit('close')
        emit('refresh')
    }
})
</script>

<template>
    <BaseDialog
        :open="open"
        @close="$emit('close')"
        @confirm="createProductionLine.createData"
        :loading="createProductionLine.loading.value"
        :title="$t('productionLine.create')"
        :disabled="!createProductionLine.data?.value?.name">
        <template #content>
            <StackLayout direction="column" :gap="8">
                <v-alert v-if="createError" type="error" class="mb-4">
                    {{ createError }}
                </v-alert>
                <StringInputField
                    :modelValue="createProductionLine.data.value.name"
                    @change="createProductionLine.handleUpdateValue({ name: $event })"
                    :label="$t('productionLine.model.name')"
                    required />
                <slot
                    name="create"
                    :data="createProductionLine.data.value"
                    @change="createProductionLine.handleUpdateValue" />
            </StackLayout>
        </template>
    </BaseDialog>
</template>
